<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import FinancingRequestWizardController from '@controllers/Investors/FinancingRequestWizardController'
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/** Die beiden Türen entsprechen dem ersten Schritt des Wizards – er startet mit der Antwort. */
const intents = [
  { key: 'purchase', origin: 'new', icon: 'pi pi-home', lead: true },
  { key: 'refinance', origin: 'portfolio', icon: 'pi pi-refresh', lead: false },
] as const

function start(origin: string): void {
  router.visit(FinancingRequestWizardController.create.url({ query: { origin } }))
}
</script>

<template>
  <section class="fh-card @container p-6">
    <p class="section-label section-label--sm">{{ t('eyebrow') }}</p>
    <h2 class="mt-1 text-xl font-bold tracking-tight">{{ t('title') }}</h2>

    <div class="mt-5 grid gap-4 @2xl:grid-cols-2">
      <div
        v-for="intent in intents"
        :key="intent.key"
        class="flex flex-col gap-3 rounded-xl border p-6"
        :class="intent.lead ? 'border-action bg-info-subtle' : 'border-line bg-surface'">
        <span
          class="flex size-9.5 flex-none items-center justify-center rounded-[10px]"
          :class="intent.lead ? 'bg-area-estate-subtle text-area-estate' : 'bg-info-subtle text-action'">
          <i :class="intent.icon" aria-hidden="true" />
        </span>

        <div>
          <p class="font-bold">{{ t(`intents.${intent.key}.title`) }}</p>
          <p class="text-ink-2 mt-1 max-w-[46ch] text-sm">{{ t(`intents.${intent.key}.description`) }}</p>
        </div>

        <div class="mt-auto pt-1">
          <FinestButton
            :variant="intent.lead ? 'gradient' : 'primary'"
            size="lg"
            icon="pi pi-arrow-right"
            icon-pos="right"
            :label="t(`intents.${intent.key}.cta`)"
            @click="start(intent.origin)" />
        </div>
      </div>
    </div>
  </section>
</template>

<i18n>
{
  "en": {
    "eyebrow": "Your plan",
    "title": "What would you like to finance?",
    "intents": {
      "purchase": {
        "title": "Buy a property",
        "description": "A new purchase, whether you have found it already or are still looking.",
        "cta": "Finance a purchase"
      },
      "refinance": {
        "title": "Refinance a property you own",
        "description": "Rollover, replacing a loan, or releasing capital from a property.",
        "cta": "Start refinancing"
      }
    }
  },
  "de": {
    "eyebrow": "Dein Anliegen",
    "title": "Was möchtest du finanzieren?",
    "intents": {
      "purchase": {
        "title": "Immobilie kaufen",
        "description": "Neukauf, ob schon gefunden oder noch auf der Suche.",
        "cta": "Kauf finanzieren"
      },
      "refinance": {
        "title": "Bestand refinanzieren",
        "description": "Prolongation, Ablösung oder Kapital aus einem Objekt ziehen.",
        "cta": "Refinanzierung starten"
      }
    }
  }
}
</i18n>
